<template>
  <v-layout wrap>
    <v-flex xs12 pb-0>
      <v-layout wrap align-center>
        <v-flex xs12 sm6 md6 lg6>
          <h1 class="headline">{{ $t('employees.title') }}</h1>
        </v-flex>

        <v-flex xs12 sm6 md6 lg6>
          <v-layout wrap align-center justify-end>
            <v-btn
              v-if="$can('employee_add')"
              color="warning mt-2"
              large
              depressed
              :disabled="filtersLoading"
              @click="dialog = true"
            >
              {{ $t('employees.add') }}
            </v-btn>
          </v-layout>
        </v-flex>
      </v-layout>

      <v-layout wrap>
        <v-flex xs12 sm12 md8 mt-4>
          <v-layout wrap align-center>
            <v-flex xs12 sm12 mt-4>
              <v-layout wrap align-center class="search-wrapper">
                <div class="d-flex search">
                  <v-text-field
                    v-model="searchString"
                    ref="search"
                    autocomplete="off"
                    solo
                    :placeholder="$t('employees.search')"
                  />
                  <v-btn
                    type="button"
                    class="search-btn warning"
                    depressed
                    large
                    @click="filters.search = searchString"
                  >
                    {{ $t('common.btns.search') }}
                  </v-btn>
                </div>
              </v-layout>
            </v-flex>
            <v-flex xs12 sm4 class="filter-wrapper">
              <v-select
                v-model="filters.role"
                item-text="displayName"
                item-value="id"
                class="filter mt-0"
                clearable
                :loading="filtersLoading"
                :label="$t('employees.role')"
                :items="roles"
              />
            </v-flex>
            <v-flex xs12 sm4 class="filter-wrapper">
              <v-select
                v-model="filters.park"
                item-text="name"
                item-value="id"
                class="filter mt-0"
                clearable
                :loading="filtersLoading"
                :label="$t('employees.park')"
                :items="parks"
              />
            </v-flex>

            <v-flex xs12 sm4 class="filter-wrapper">
              <v-checkbox
                v-if="isAdmin"
                v-model="filters.withTrashed"
                color="warning"
                class="ml-auto"
                true-value="1"
                false-value="0"
                :label="$t('common.labels.showDeleted')"
              />
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>

      <v-flex mt-5 mb-5 xs12>
        <v-data-table
          :headers="headers"
          :items="list"
          :loading="loading"
          :items-per-page="perPage"
          hide-default-footer
        >
          <template #item="{item}">
            <tr :class="{ 'deleted-row': item.deleted_at }">
              <td v-if="canDelete" width="50">
                <div class="d-flex">
                  <v-btn
                    v-if="item.deleted_at"
                    class="restore-btn"
                    color="warning"
                    small
                    icon
                    :loading="restoreIds.includes(item.id)"
                    @click="
                      () => {
                        confirmDialog.value = true;
                        confirmDialog.title = $t('common.restore') + ' ?';
                        confirmDialog.action = () => restoreItem(item.id);
                      }
                    "
                  >
                    <v-icon small>mdi-restore</v-icon>
                  </v-btn>
                  <v-btn
                    v-else
                    color="red"
                    small
                    icon
                    :loading="deletingIds.includes(item.id)"
                    @click="
                      () => {
                        confirmDialog.action = () => deleteItem(item.id);
                        confirmDialog.title = $t('common.btns.delete');
                        confirmDialog.value = true;
                      }
                    "
                  >
                    <v-icon small>mdi-trash-can-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="$can('employee_edit')"
                    color="warning"
                    small
                    icon
                    @click="
                      () => {
                        resetPassword.dialog = true;
                        resetPassword.id = item.id;
                      }
                    "
                  >
                    <v-icon small>mdi-lock-reset</v-icon>
                  </v-btn>
                </div>
              </td>

              <td>{{ item.num }}</td>

              <td>
                <button
                  class="employees-list__btn"
                  @click="
                    () => {
                      selectedItem = item;
                      dialog = true;
                    }
                  "
                >
                  {{ item.user.name || '-' }}
                </button>
              </td>

              <td>{{ item.user.email || '-' }}</td>
              <td>{{ item.user.phone || '-' }}</td>
              <td>{{ getRoles(item.user.roles) || '-' }}</td>
              <td>{{ item.parks.map(({ name }) => name).toString() }}</td>
            </tr>
          </template>

          <template #footer>
            <v-divider />
            <div class="pt-1 pb-2">
              <v-pagination
                color="warning"
                :value="currentPage"
                :length="lastPage"
                dark
                circle
                @input="fetchList({ ...filters, page: $event })"
              />
            </div>
          </template>
        </v-data-table>
      </v-flex>
    </v-flex>

    <add-employees-dialog
      v-model="dialog"
      :item="selectedItem"
      @updated="onUpdate"
      @close="selectedItem = null"
    />

    <reset-password-dialog
      v-model="resetPassword.dialog"
      :id="resetPassword.id"
      @updated="onPassUpdate"
    />

    <confirm-dialog
      v-model="confirmDialog.value"
      :title="confirmDialog.title"
      @confirm="confirmDialog.action"
    />

    <v-snackbar v-model="snack.value" :color="snack.color" bottom right>
      {{ snack.message }}
    </v-snackbar>
  </v-layout>
</template>

<script>
import { mapState } from 'vuex';
import AddEmployeesDialog from '@/components/employees/AddEmployeesDialog.vue';
import ResetPasswordDialog from '@/components/employees/ResetPasswordDialog.vue';
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue';

export default {
  components: { AddEmployeesDialog, ResetPasswordDialog, ConfirmDialog },
  data: () => ({
    loading: false,
    filtersLoading: false,
    dialog: false,
    selectedItem: null,
    deletingIds: [],
    restoreIds: [],
    confirmDialog: {
      value: false,
      title: '',
      action: () => {},
    },

    resetPassword: {
      dialog: false,
      id: '',
    },

    searchString: '',

    filters: {
      search: '',
      park: '',
      role: '',
      withTrashed: 0,
    },

    snack: {
      value: false,
      message: '',
      color: '',
    },
  }),
  watch: {
    filters: {
      immediate: true,
      deep: true,
      async handler(params) {
        this.fetchList(params);
      },
    },
  },
  computed: {
    ...mapState({
      list: ({ employees }) => employees.list,
      currentPage: ({ employees }) => employees.currentPage,
      lastPage: ({ employees }) => employees.lastPage,
      perPage: ({ employees }) => employees.perPage,
      role: ({ global }) => global.role,
      roles: ({ roles }) => roles.list,
      parks: ({ parks }) => parks.list,
    }),
    canDelete() {
      return this.role === 'admin';
    },
    headers() {
      const headers = [
        { text: this.$t('common.number'), sortable: false },
        { text: this.$t('common.fields.investorFullName'), sortable: false },
        { text: this.$t('investorsList.tableHeaders.email'), sortable: false },
        { text: this.$t('common.fields.phone'), sortable: false },
        { text: this.$t('employees.role'), sortable: false },
        { text: this.$t('employees.park'), sortable: false },
      ];

      if (this.canDelete) {
        headers.unshift({ text: '', sortable: false });
      }

      return headers;
    },
    isAdmin() {
      return this.$store.getters['global/getRole'] === 'admin';
    },
  },
  methods: {
    async fetchList(params) {
      this.loading = true;
      await this.$store.dispatch('employees/fetchList', params);
      this.loading = false;
    },
    showSnack({ error }) {
      const snack = {
        value: true,
        color: 'success',
        message: this.$t('common.saved'),
      };

      if (error) {
        snack.color = 'error';
        snack.message = this.$t('common.errors.APIerror');
      }

      this.snack = snack;
    },
    async onUpdate({ success, error }) {
      await this.fetchList({ ...this.fetchList, page: this.currentPage });
      this.showSnack({ error });
      if (success) this.dialog = false;
    },
    onPassUpdate({ success, error }) {
      this.showSnack({ error });
      if (success) this.resetPassword.dialog = false;
    },
    async deleteItem(id) {
      this.deletingIds.push(id);
      const { success, error } = await this.$store.dispatch(
        'employees/deleteItem',
        id,
      );

      if (success) {
        await this.fetchList({ ...this.filters, page: this.currentPage });
      }

      this.showSnack({ error });
      this.deletingIds = this.deletingIds.filter((item) => item !== id);
    },
    async restoreItem(id) {
      this.restoreIds.push(id);
      const { success, error } = await this.$store.dispatch(
        'employees/restoreItem',
        id,
      );

      if (success) {
        await this.fetchList({ ...this.filters, page: this.currentPage });
      }

      this.showSnack({ error });
      this.restoreIds = this.restoreIds.filter((item) => item !== id);
    },
    getRoles(array) {
      const roles = array.map(({ displayName }) => displayName);
      return roles.toString();
    },
  },
  async created() {
    this.filtersLoading = true;
    await this.$store.dispatch('roles/fetchList');
    await this.$store.dispatch('parks/fetchList');
    this.filtersLoading = false;
  },
};
</script>

<style lang="scss">
.search {
  max-width: 450px;
  width: 100%;
}

.employees-list {
  &__btn {
    color: #424242;
    font-weight: 600;
    text-decoration: underline;
    text-align: left;

    &:hover:not([disabled]) {
      text-decoration: none;
    }

    &[disabled] {
      opacity: 0.7;
    }
  }
}

.confirm-title {
  width: 100%;
  text-align: center;
  font-size: 14;
}

.v-pagination__item,
.v-pagination__navigation {
  box-shadow: none;
}
</style>
